import React, { useEffect, useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { api, formatDateFR } from "../lib/api";
import { toast } from "sonner";
import {
  Copy, Download, Star, Eye, EyeOff, Trash2, Share2, QrCode,
  MessageSquareHeart, Mail, Loader2, AlertTriangle,
} from "lucide-react";

export default function VendreMarketing({ pageUrl, published }) {
  const qrRef = useRef(null);
  const [reviews, setReviews] = useState(null);

  const load = () => {
    api.get("/vendor/reviews").then(({ data }) => setReviews(data.reviews || [])).catch(() => setReviews([]));
  };
  useEffect(load, []);

  if (!published || !pageUrl) {
    return (
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 flex items-start gap-3" data-testid="marketing-not-published">
        <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
        <div className="text-xs text-amber-800">
          <span className="font-semibold">Votre page n'est pas encore en ligne.</span> Publiez-la depuis « Publication » pour activer le QR code, le partage et les demandes d'avis.
        </div>
      </div>
    );
  }

  const reviewUrl = `${pageUrl}?avis=1`;
  const shareText = encodeURIComponent(`Réservez votre voiture en ligne : ${pageUrl}`);
  const reviewText = encodeURIComponent(`Merci pour votre location ! Pouvez-vous nous laisser un avis ? ${reviewUrl}`);

  const copy = (text, msg) => { navigator.clipboard.writeText(text); toast.success(msg); };

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "qr-code-boutique.png";
    a.click();
    toast.success("QR code téléchargé");
  };

  const toggleReview = async (r) => {
    try {
      await api.patch(`/vendor/reviews/${r.id}`, { published: !r.published });
      setReviews((list) => list.map((x) => (x.id === r.id ? { ...x, published: !r.published } : x)));
      toast.success(!r.published ? "Avis affiché sur votre boutique" : "Avis masqué");
    } catch { toast.error("Erreur"); }
  };

  const deleteReview = async (r) => {
    if (!window.confirm("Supprimer définitivement cet avis ?")) return;
    try {
      await api.delete(`/vendor/reviews/${r.id}`);
      setReviews((list) => list.filter((x) => x.id !== r.id));
      toast.success("Avis supprimé");
    } catch { toast.error("Erreur"); }
  };

  const publishedReviews = (reviews || []).filter((r) => r.published);
  const avg = publishedReviews.length
    ? (publishedReviews.reduce((s, r) => s + r.rating, 0) / publishedReviews.length).toFixed(1)
    : null;

  return (
    <div className="space-y-6" data-testid="vendre-marketing">
      {/* ─── QR Code ─── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="marketing-qr-card">
        <div className="flex items-center gap-2 mb-4">
          <QrCode className="h-4 w-4 text-slate-500" />
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">QR code de votre boutique</div>
        </div>
        <div className="flex flex-col sm:flex-row items-center gap-6">
          <div ref={qrRef} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-testid="marketing-qr-canvas">
            <QRCodeCanvas value={pageUrl} size={168} level="M" includeMargin={false} fgColor="#0f172a" />
            <div className="mt-2 text-center text-[9px] font-semibold tracking-wider text-slate-400 uppercase">Propulsé par Junago</div>
          </div>
          <div className="flex-1 space-y-3 text-center sm:text-left">
            <p className="text-xs text-slate-500 leading-relaxed">
              Imprimez-le en agence, sur vos flyers ou vos véhicules : vos clients scannent et réservent directement en ligne.
            </p>
            <div className="flex flex-wrap justify-center sm:justify-start gap-2">
              <button onClick={downloadQr} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-900 text-white text-xs font-bold hover:bg-slate-800 transition-colors" data-testid="marketing-qr-download">
                <Download className="h-3.5 w-3.5" /> Télécharger le QR code
              </button>
              <button onClick={() => copy(pageUrl, "Lien copié !")} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition-colors" data-testid="marketing-qr-copy-link">
                <Copy className="h-3.5 w-3.5" /> Copier le lien
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Partage ─── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="marketing-share-card">
        <div className="flex items-center gap-2 mb-3">
          <Share2 className="h-4 w-4 text-slate-500" />
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Partager votre boutique</div>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`https://wa.me/?text=${shareText}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#25D366]/10 text-[#128C4A] text-xs font-bold hover:bg-[#25D366]/20 transition-colors" data-testid="marketing-share-whatsapp">
            <i className="fab fa-whatsapp text-sm" /> WhatsApp
          </a>
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#1877F2]/10 text-[#1877F2] text-xs font-bold hover:bg-[#1877F2]/20 transition-colors" data-testid="marketing-share-facebook">
            <i className="fab fa-facebook text-sm" /> Facebook
          </a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#0A66C2]/10 text-[#0A66C2] text-xs font-bold hover:bg-[#0A66C2]/20 transition-colors" data-testid="marketing-share-linkedin">
            <i className="fab fa-linkedin text-sm" /> LinkedIn
          </a>
          <button onClick={() => { copy(pageUrl, "Lien copié — collez-le dans votre bio Instagram"); window.open("https://www.instagram.com/", "_blank"); }} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-gradient-to-r from-fuchsia-500/10 to-amber-400/10 text-fuchsia-600 text-xs font-bold hover:from-fuchsia-500/20 hover:to-amber-400/20 transition-colors" data-testid="marketing-share-instagram">
            <i className="fab fa-instagram text-sm" /> Instagram
          </button>
          <a href={`mailto:?subject=${encodeURIComponent("Réservez votre voiture en ligne")}&body=${shareText}`} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition-colors" data-testid="marketing-share-email">
            <Mail className="h-3.5 w-3.5" /> Email
          </a>
        </div>
      </div>

      {/* ─── Demande d'avis ─── */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="marketing-reviews-card">
        <div className="flex items-center gap-2 mb-3">
          <MessageSquareHeart className="h-4 w-4 text-slate-500" />
          <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Avis clients</div>
          {avg && (
            <span className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-50 border border-amber-100 text-amber-700 text-[11px] font-bold" data-testid="marketing-reviews-avg">
              <Star className="h-3 w-3 fill-amber-400 text-amber-400" /> {avg}/5 · {publishedReviews.length} avis
            </span>
          )}
        </div>
        <p className="text-xs text-slate-500 leading-relaxed mb-3">
          Envoyez ce lien à vos clients après leur location : ils laissent une note et un commentaire, affichés sur votre boutique pour rassurer les futurs clients.
        </p>
        <div className="flex flex-wrap gap-2 mb-5">
          <button onClick={() => copy(reviewUrl, "Lien de demande d'avis copié !")} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-violet-600 text-white text-xs font-bold hover:bg-violet-500 transition-colors" data-testid="marketing-review-copy">
            <Copy className="h-3.5 w-3.5" /> Copier le lien d'avis
          </button>
          <a href={`https://wa.me/?text=${reviewText}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#25D366]/10 text-[#128C4A] text-xs font-bold hover:bg-[#25D366]/20 transition-colors" data-testid="marketing-review-whatsapp">
            <i className="fab fa-whatsapp text-sm" /> WhatsApp
          </a>
          <a href={`mailto:?subject=${encodeURIComponent("Votre avis compte pour nous !")}&body=${reviewText}`} className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-100 text-slate-700 text-xs font-bold hover:bg-slate-200 transition-colors" data-testid="marketing-review-email">
            <Mail className="h-3.5 w-3.5" /> Email
          </a>
        </div>

        {/* Liste des avis reçus */}
        {reviews === null ? (
          <div className="py-6 flex justify-center"><Loader2 className="h-4 w-4 animate-spin text-violet-500" /></div>
        ) : reviews.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-200 py-8 text-center text-xs text-slate-400" data-testid="marketing-reviews-empty">
            Aucun avis reçu pour l'instant — partagez le lien ci-dessus à vos clients.
          </div>
        ) : (
          <div className="space-y-2" data-testid="marketing-reviews-list">
            {reviews.map((r) => (
              <div key={r.id} className={`rounded-xl border p-3 flex items-start gap-3 ${r.published ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-70"}`} data-testid={`marketing-review-${r.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-bold text-slate-900">{r.name}</span>
                    <span className="inline-flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <Star key={i} className={`h-3 w-3 ${i <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />
                      ))}
                    </span>
                    <span className="text-[10px] text-slate-400">{formatDateFR(r.created_at?.slice(0, 10))}</span>
                    {!r.published && <span className="px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-500 text-[9px] font-bold">Masqué</span>}
                  </div>
                  {r.comment && <div className="mt-1 text-xs text-slate-500 leading-relaxed">{r.comment}</div>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => toggleReview(r)} title={r.published ? "Masquer de la boutique" : "Afficher sur la boutique"} className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors" data-testid={`marketing-review-toggle-${r.id}`}>
                    {r.published ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
                  </button>
                  <button onClick={() => deleteReview(r)} title="Supprimer" className="h-7 w-7 rounded-lg flex items-center justify-center text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors" data-testid={`marketing-review-delete-${r.id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
